import { Controller } from "@hotwired/stimulus"
import { usesSidebarLayout } from "../utils/layout_media"
import { csrfToken } from "../utils/csrf"

// Tinder-style deck for the dating Discover page, forked from
// card_stack_controller. Cards are server-rendered into the viewport; the
// controller keeps a 3-card stack, drags/flies the top card (right = Like,
// left = Nope), posts swipes, shows the match interstitial, and supports
// undo (the server rejects rewinds that already created a match).
// On desktop the Info button opens a right sidebar (same .detail-sidebar
// chrome as Messages) with the top card's full profile, fetched from
// DatingController#profile_panel and kept in sync as cards fly off.
export default class extends Controller {
    static targets = ["viewport", "error", "empty", "matchOverlay", "matchName", "matchAvatar", "matchAvatarPlaceholder", "matchLink", "undoBtn", "likeBtn", "nopeBtn", "infoBtn"]
    static values = { swipeUrl: String, undoUrl: String, profilePanelUrl: String }

    STACK_SIZE = 3
    CLAIM_PX = 24
    THRESHOLD_RATIO = 0.3
    FLICK_VELOCITY = 0.5 // px/ms
    ROTATE_DEG_PER_PX = 0.05
    FLY_MS = 300

    connect() {
        this._cards = [] // rendered card elements, index 0 = top
        this._queue = [] // detached card elements not yet in the stack
        this._history = [] // { card, direction, matched } newest last
        this._swipe = null
        this._busyUndo = false
        this._onKeydown = this._handleKeydown.bind(this)
        document.addEventListener("keydown", this._onKeydown)
        this._onBeforeCache = () => this._closeProfileSidebar()
        document.addEventListener("turbo:before-cache", this._onBeforeCache)

        let all = Array.from(this.viewportTarget.querySelectorAll(".dating-card"))
        all.forEach((card, i) => {
            if (i >= this.STACK_SIZE) {
                card.remove()
                this._queue.push(card)
            } else {
                this._cards.push(card)
            }
        })
        this._layout()
        this._updateChrome()

        let vp = this.viewportTarget
        vp.addEventListener("touchstart", (e) => this._touchStart(e), { passive: true })
        vp.addEventListener("touchmove", (e) => this._touchMove(e), { passive: false })
        vp.addEventListener("touchend", () => this._endSwipe())
        vp.addEventListener("touchcancel", () => this._endSwipe({ cancel: true }))
        vp.addEventListener("pointerdown", (e) => this._pointerDown(e))
        vp.addEventListener("pointermove", (e) => this._pointerMove(e))
        vp.addEventListener("pointerup", (e) => this._pointerUp(e))
        vp.addEventListener("pointercancel", (e) => this._pointerUp(e, { cancel: true }))
        // Runs after _suppressNextClick's capture-phase swallow, so photo taps
        // only fire on clean taps — never at the end of a drag.
        vp.addEventListener("click", (e) => this._photoTap(e))
    }

    disconnect() {
        document.removeEventListener("keydown", this._onKeydown)
        document.removeEventListener("turbo:before-cache", this._onBeforeCache)
        this._closeProfileSidebar()
    }

    // ----- Button actions -----

    like() {
        this._act("like")
    }

    nope() {
        this._act("pass")
    }

    info() {
        let card = this._topCard()
        if (!card) return
        if (this._useProfileSidebar()) {
            this._toggleProfileSidebar()
            return
        }
        let content = card.querySelector(".dating-card-content")
        if (!content) return
        let photo = card.querySelector(".dating-card-photo")
        let offset = photo ? photo.offsetHeight - 60 : 200
        content.scrollTo({ top: content.scrollTop > 10 ? 0 : offset, behavior: "smooth" })
    }

    // ----- Desktop profile sidebar (mirrors the Messages msg-detail sidebar) -----

    _useProfileSidebar() {
        return this.hasProfilePanelUrlValue && usesSidebarLayout()
    }

    _toggleProfileSidebar() {
        if (this._profileSidebar) {
            this._closeProfileSidebar()
        } else {
            this._openProfileSidebar()
        }
    }

    _openProfileSidebar() {
        let card = this._topCard()
        let phone = document.querySelector(".assistant-phone")
        if (!card || !phone) return

        let sidebar = document.createElement("aside")
        // dating-profile-panel handles the photo tap zones inside the fetched panel
        sidebar.className = "detail-sidebar detail-sidebar--dating"
        sidebar.setAttribute("role", "complementary")
        sidebar.setAttribute("aria-label", "Profile details")
        sidebar.setAttribute("data-controller", "dating-profile-panel")
        sidebar.innerHTML = `
            <div class="detail-sidebar-actions">
                <button type="button" class="detail-sidebar-close assistant-header-btn assistant-filter-toggle assistant-avatar" aria-label="Close profile">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>
            <div class="detail-sidebar-content"></div>`
        sidebar.querySelector(".detail-sidebar-close").addEventListener("click", () => this._closeProfileSidebar())
        phone.appendChild(sidebar)
        phone.classList.add("is-detail-open")
        this._profileSidebar = sidebar
        this._loadProfileSidebar(card)
    }

    _closeProfileSidebar() {
        if (!this._profileSidebar) return
        this._profileSidebar.remove()
        this._profileSidebar = null
        this._profileSidebarId = null
        document.querySelector(".assistant-phone")?.classList.remove("is-detail-open")
    }

    _mountActionsMenu(content) {
        if (!this._profileSidebar) return
        let menu = content?.querySelector(".msg-detail-menu")
        if (!menu) return
        let actions = this._profileSidebar.querySelector(".detail-sidebar-actions")
        let closeBtn = actions?.querySelector(".detail-sidebar-close")
        if (!actions || !closeBtn) return
        this._unmountActionsMenu()
        actions.insertBefore(menu, closeBtn)
    }

    _unmountActionsMenu() {
        this._profileSidebar?.querySelectorAll(".detail-sidebar-actions .msg-detail-menu")
            .forEach((el) => el.remove())
    }

    // Keeps the open sidebar pointed at the current top card: refetch after a
    // swipe/undo changes the top, close when the deck runs out.
    _syncProfileSidebar() {
        if (!this._profileSidebar) return
        let card = this._topCard()
        if (!card) {
            this._closeProfileSidebar()
            return
        }
        if (this._profileSidebarId === card.dataset.profileId) return
        this._loadProfileSidebar(card)
    }

    _loadProfileSidebar(card) {
        let content = this._profileSidebar.querySelector(".detail-sidebar-content")
        this._profileSidebarId = card.dataset.profileId
        this._unmountActionsMenu()
        content.innerHTML = `
            <div class="msg-detail-loading">
                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                <span>Loading…</span>
            </div>`

        let token = (this._profileLoadToken = (this._profileLoadToken || 0) + 1)
        let url = `${this.profilePanelUrlValue}&profile_id=${encodeURIComponent(card.dataset.profileId)}`
        fetch(url, { headers: { "Accept": "text/html" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((html) => {
                if (token !== this._profileLoadToken || !this._profileSidebar) return
                content.innerHTML = html
                this._mountActionsMenu(content)
            })
            .catch(() => {
                if (token !== this._profileLoadToken || !this._profileSidebar) return
                content.innerHTML = `
                    <div class="msg-detail-loading msg-detail-loading--error">
                        <span>Could not load this profile.</span>
                    </div>`
            })
    }

    dismissMatch() {
        this.matchOverlayTarget.classList.add("d-none")
    }

    // ----- Photo stepper (Tinder-style: tap the right/left half of the photo
    // to step through the stack; dots at the top show progress) -----

    _photoTap(e) {
        let zone = e.target.closest("[data-photo-step]")
        if (!zone) return
        let card = zone.closest(".dating-card")
        if (!card || card !== this._topCard() || card.dataset.stackFlying) return
        e.preventDefault()
        e.stopPropagation()
        this._stepPhoto(card, parseInt(zone.dataset.photoStep, 10))
    }

    _stepPhoto(card, delta) {
        let imgs = Array.from(card.querySelectorAll(".dating-card-photo-img"))
        if (imgs.length < 2) return
        let current = imgs.findIndex((img) => !img.classList.contains("d-none"))
        let next = Math.min(Math.max(current + delta, 0), imgs.length - 1)
        if (next === current) return
        imgs.forEach((img, i) => img.classList.toggle("d-none", i !== next))
        card.querySelectorAll(".dating-photo-dot").forEach((dot, i) => dot.classList.toggle("is-active", i === next))
    }

    // ----- Swipe actions -----

    _act(direction, opts = {}) {
        let card = this._topCard()
        if (!card || card.dataset.stackFlying) return

        let entry = { card: card, direction: direction, matched: false }
        this._history.push(entry)
        this._postSwipe(card, direction, entry)
        if (!opts.fromSwipe) this._flashBadge(card, direction === "like" ? "like" : "nope")
        this._flyOff(card, direction)
    }

    _postSwipe(card, direction, entry) {
        fetch(this.swipeUrlValue, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ target_id: card.dataset.profileId, direction: direction })
        })
            .then((r) => r.json())
            .then((data) => {
                if (!data.ok) {
                    this._showError(data.error || "Something went wrong - that swipe wasn't saved.")
                    return
                }
                if (data.matched) {
                    entry.matched = true
                    this._showMatch(data)
                }
            })
            .catch(() => this._showError("Network hiccup - that swipe may not have been saved."))
    }

    undo() {
        if (this._busyUndo) return
        let entry = this._history[this._history.length - 1]
        if (!entry) return
        if (entry.matched) {
            this._showError("Can't undo - you already matched!")
            return
        }

        this._busyUndo = true
        fetch(this.undoUrlValue, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ target_id: entry.card.dataset.profileId })
        })
            .then((r) => r.json().then((data) => ({ ok: r.ok, data: data })))
            .then(({ ok, data }) => {
                if (ok && data.ok) {
                    this._history.pop()
                    this._readopt(entry.card)
                } else {
                    this._showError(data.error || "Couldn't undo that swipe.")
                    if (data.error && data.error.indexOf("match") !== -1) entry.matched = true
                }
            })
            .catch(() => this._showError("Network hiccup - couldn't undo."))
            .finally(() => {
                this._busyUndo = false
                this._updateChrome()
            })
    }

    // ----- Stack management (ported from card_stack) -----

    _topCard() {
        return this._cards[0] || null
    }

    _layout() {
        this._cards.forEach((card, i) => {
            card.style.setProperty("--stack-depth", i)
            card.style.zIndex = 30 - i
            card.classList.toggle("is-top", i === 0)
        })
    }

    _fillStack() {
        while (this._cards.length < this.STACK_SIZE && this._queue.length) {
            let card = this._queue.shift()
            this.viewportTarget.appendChild(card)
            this._cards.push(card)
        }
        this._layout()
        if (!this._cards.length) this.emptyTarget.classList.remove("d-none")
    }

    _flyOff(card, direction) {
        card.dataset.stackFlying = "1"
        card.classList.remove("is-dragging")
        let w = card.offsetWidth
        let sign = direction === "like" ? 1 : -1
        card.style.transition = `transform ${this.FLY_MS / 1000}s cubic-bezier(0.22, 0.61, 0.36, 1), opacity ${this.FLY_MS / 1000}s ease-out`
        card.style.transform = `translateX(${sign * (window.innerWidth + w)}px) rotate(${sign * 24}deg)`
        card.style.opacity = "0"
        this._cards.shift()
        this._layout()
        this._updateChrome()
        this._syncProfileSidebar()
        setTimeout(() => {
            if (card.dataset.stackFlying) card.remove() // kept alive via the history entry for undo
            this._fillStack()
            this._updateChrome()
        }, this.FLY_MS)
    }

    _readopt(card) {
        this.emptyTarget.classList.add("d-none")

        // Keep the stack at STACK_SIZE: push the deepest card back to the queue.
        if (this._cards.length >= this.STACK_SIZE) {
            let deepest = this._cards.pop()
            this._queue.unshift(deepest)
            deepest.remove()
        }

        delete card.dataset.stackFlying
        card.querySelectorAll(".stack-badge").forEach((b) => {
            b.style.transition = ""
            b.style.opacity = ""
        })
        card.style.transition = "none"
        this.viewportTarget.appendChild(card)
        this._cards.unshift(card)
        this._layout()
        void card.offsetWidth
        card.style.transition = `transform ${this.FLY_MS / 1000}s cubic-bezier(0.22, 0.61, 0.36, 1), opacity ${this.FLY_MS / 1000}s ease-out`
        card.style.transform = ""
        card.style.opacity = ""
        setTimeout(() => {
            card.style.transition = ""
        }, this.FLY_MS)
        this._updateChrome()
        this._syncProfileSidebar()
    }

    _updateChrome() {
        this.undoBtnTarget.disabled = !this._history.length || !!(this._history[this._history.length - 1] || {}).matched
        let hasTop = !!this._topCard()
        this.likeBtnTarget.disabled = !hasTop
        this.nopeBtnTarget.disabled = !hasTop
        this.infoBtnTarget.disabled = !hasTop
    }

    _flashBadge(card, kind) {
        let badge = card.querySelector(`.stack-badge--${kind}`)
        if (!badge) return
        badge.style.transition = "opacity 0.15s ease"
        badge.style.opacity = "1"
    }

    // ----- Match interstitial -----

    _showMatch(data) {
        let peer = data.peer || {}
        this.matchNameTarget.textContent = `You and ${peer.first_name || "your match"} liked each other`
        if (peer.avatar_url) {
            this.matchAvatarTarget.src = peer.avatar_url
            this.matchAvatarTarget.classList.remove("d-none")
            this.matchAvatarPlaceholderTarget.classList.add("d-none")
        } else {
            this.matchAvatarTarget.classList.add("d-none")
            this.matchAvatarPlaceholderTarget.textContent = (peer.first_name || "?").slice(0, 1).toUpperCase()
            this.matchAvatarPlaceholderTarget.classList.remove("d-none")
        }
        this.matchLinkTarget.href = data.match_url
        this.matchOverlayTarget.classList.remove("d-none")
        this._updateChrome()
    }

    _showError(text) {
        this.errorTarget.textContent = text
        this.errorTarget.classList.remove("d-none")
        clearTimeout(this._errorTimer)
        this._errorTimer = setTimeout(() => this.errorTarget.classList.add("d-none"), 3500)
    }

    // ----- Drag (ported from card_stack: follow-the-finger translate +
    // rotation, badges fade with progress, past-threshold or flick flies) -----

    _touchStart(e) {
        if (e.touches.length !== 1) return
        this._beginSwipe(e.touches[0].clientX, e.touches[0].clientY, e.timeStamp)
    }

    _pointerDown(e) {
        if (e.pointerType === "touch" || !e.isPrimary || e.button !== 0) return
        this._beginSwipe(e.clientX, e.clientY, e.timeStamp, { pointerId: e.pointerId, captureTarget: e.currentTarget })
    }

    _beginSwipe(clientX, clientY, timeStamp, opts = {}) {
        let card = this._topCard()
        if (!card || card.dataset.stackFlying) return
        if (!this.matchOverlayTarget.classList.contains("d-none")) return
        this._swipe = {
            card: card,
            startX: clientX,
            startY: clientY,
            width: card.offsetWidth,
            claimed: false,
            dx: 0,
            lastX: clientX,
            lastT: timeStamp,
            velocity: 0,
            raf: null,
            pointerId: opts.pointerId != null ? opts.pointerId : null,
            captureTarget: opts.captureTarget || null
        }
        if (this._swipe.captureTarget && this._swipe.pointerId != null) {
            try { this._swipe.captureTarget.setPointerCapture(this._swipe.pointerId) } catch { /* detached */ }
        }
    }

    _touchMove(e) {
        this._moveSwipe(e.touches[0].clientX, e.touches[0].clientY, e.timeStamp, e)
    }

    _pointerMove(e) {
        if (!this._swipe || e.pointerType === "touch" || e.pointerId !== this._swipe.pointerId) return
        this._moveSwipe(e.clientX, e.clientY, e.timeStamp, e)
    }

    _moveSwipe(x, y, timeStamp, e) {
        let s = this._swipe
        if (!s) return
        let dx = x - s.startX
        let dy = y - s.startY

        if (!s.claimed) {
            if (Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx)) {
                this._swipe = null
                return
            }
            if (Math.abs(dx) < this.CLAIM_PX || Math.abs(dx) < Math.abs(dy) * 1.2) return
            s.claimed = true
            s.card.classList.add("is-dragging")
        }

        e.preventDefault()
        if (timeStamp > s.lastT) s.velocity = (x - s.lastX) / (timeStamp - s.lastT)
        s.lastX = x
        s.lastT = timeStamp
        s.dx = dx
        if (!s.raf) {
            s.raf = requestAnimationFrame(() => {
                s.raf = null
                this._renderDrag(s)
            })
        }
    }

    _renderDrag(s) {
        let dx = s.dx
        let progress = Math.min(Math.abs(dx) / (s.width * this.THRESHOLD_RATIO), 1)
        s.card.style.transform = `translateX(${dx}px) rotate(${(dx * this.ROTATE_DEG_PER_PX).toFixed(2)}deg)`
        s.card.style.opacity = `${1 - 0.25 * progress}`
        let like = s.card.querySelector(".stack-badge--like")
        let nope = s.card.querySelector(".stack-badge--nope")
        if (like) like.style.opacity = dx > 0 ? progress : 0
        if (nope) nope.style.opacity = dx < 0 ? progress : 0
    }

    _pointerUp(e, opts = {}) {
        if (!this._swipe || e.pointerType === "touch" || e.pointerId !== this._swipe.pointerId) return
        this._endSwipe(opts)
    }

    _endSwipe(opts = {}) {
        let s = this._swipe
        this._swipe = null
        if (!s || !s.claimed) return
        if (s.raf) cancelAnimationFrame(s.raf)
        this._suppressNextClick()

        let pastThreshold = Math.abs(s.dx) >= s.width * this.THRESHOLD_RATIO
        let flick = Math.abs(s.velocity) >= this.FLICK_VELOCITY && (s.velocity > 0) === (s.dx > 0)
        if (!opts.cancel && (pastThreshold || flick)) {
            this._act(s.dx > 0 ? "like" : "pass", { fromSwipe: true })
        } else {
            this._springBack(s)
        }
    }

    _springBack(s) {
        s.card.classList.remove("is-dragging")
        s.card.style.transform = ""
        s.card.style.opacity = ""
        s.card.querySelectorAll(".stack-badge").forEach((b) => {
            b.style.transition = "opacity 0.2s ease"
            b.style.opacity = "0"
        })
        setTimeout(() => {
            s.card.querySelectorAll(".stack-badge").forEach((b) => (b.style.transition = ""))
        }, 220)
    }

    _suppressNextClick() {
        let swallow = (e) => {
            e.stopPropagation()
            e.preventDefault()
        }
        document.addEventListener("click", swallow, { capture: true, once: true })
        setTimeout(() => document.removeEventListener("click", swallow, { capture: true }), 400)
    }

    // ----- Keyboard (ArrowRight like, ArrowLeft nope, i info, z/u undo) -----

    _handleKeydown(e) {
        if (e.metaKey || e.ctrlKey || e.altKey) return
        let t = e.target
        if (t && t.matches && (t.matches("input, textarea, select") || t.isContentEditable)) return
        if (e.key === "Escape" && this._profileSidebar && this.matchOverlayTarget.classList.contains("d-none")) {
            e.preventDefault()
            this._closeProfileSidebar()
            return
        }
        if (!this.matchOverlayTarget.classList.contains("d-none")) {
            if (e.key === "Escape") {
                e.preventDefault()
                this.dismissMatch()
            }
            return
        }

        if (e.key === "ArrowRight") {
            e.preventDefault()
            this.like()
        } else if (e.key === "ArrowLeft") {
            e.preventDefault()
            this.nope()
        } else if (e.key === "i") {
            e.preventDefault()
            this.info()
        } else if (e.key === "z" || e.key === "u") {
            e.preventDefault()
            this.undo()
        }
    }

}
